// Copy retained pixels by row. The destination starts transparent, so uncovered
// edges can be filled independently by the renderer.
export function copyPanPixels(source, destination, width, height, offsetX, offsetY) {
  const srcX = Math.max(0, offsetX);
  const dstX = Math.max(0, -offsetX);
  const copyWidth = width - Math.abs(offsetX);
  if (copyWidth <= 0) return;
  const endY = Math.min(height, height + offsetY);
  for (let srcY = Math.max(0, offsetY), dstY = Math.max(0, -offsetY); srcY < endY; srcY++, dstY++) {
    const start = (srcY * width + srcX) * 4;
    destination.set(source.subarray(start, start + copyWidth * 4), (dstY * width + dstX) * 4);
  }
}

// Present completed worker tiles without reading the source canvas back to JS.
// Ordinary pans move retained pixels in the canvas backend and upload only the
// newly exposed tiles. A no-retention plan assembles one full upload.
export function presentPanTiles(canvas, context, result) {
  const { pixelOffsetX: dx, pixelOffsetY: dy, regions } = result;
  if (dx === 0 && dy === 0 && regions.length) {
    const image = context.createImageData(canvas.width, canvas.height);
    for (const region of regions) {
      const rowBytes = region.width * 4;
      for (let row = 0; row < region.height; row++) {
        image.data.set(region.pixels.subarray(row * rowBytes, (row + 1) * rowBytes),
          ((region.y + row) * canvas.width + region.x) * 4);
      }
    }
    context.putImageData(image, 0, 0);
    return;
  }
  if (dx !== 0 || dy !== 0) {
    const sourceX = Math.max(0, dx);
    const sourceY = Math.max(0, dy);
    const destinationX = Math.max(0, -dx);
    const destinationY = Math.max(0, -dy);
    const retainedWidth = canvas.width - Math.abs(dx);
    const retainedHeight = canvas.height - Math.abs(dy);
    context.save();
    try {
      // Source-over would leave stale destination colors underneath the
      // transparent interior of the fractal. Copy replaces those pixels too.
      context.globalCompositeOperation = 'copy';
      context.drawImage(canvas, sourceX, sourceY, retainedWidth, retainedHeight,
        destinationX, destinationY, retainedWidth, retainedHeight);
    } finally {
      context.restore();
    }
  }
  for (const region of regions) {
    context.putImageData(new ImageData(region.pixels, region.width, region.height),
      region.x, region.y);
  }
}

export function planPan({ canvasWidth: w, canvasHeight: h, oldXRange, oldYRange, newXRange, newYRange }) {
  const spanX = newXRange[1] - newXRange[0];
  const spanY = newYRange[1] - newYRange[0];
  const dx = Math.round((newXRange[0] - oldXRange[0]) / (spanX / w));
  const dy = Math.round((newYRange[0] - oldYRange[0]) / (spanY / h));
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) >= w || Math.abs(dy) >= h ||
      Math.abs(spanX - (oldXRange[1] - oldXRange[0])) > 1e-10 ||
      Math.abs(spanY - (oldYRange[1] - oldYRange[0])) > 1e-10) {
    return { pixelOffsetX: 0, pixelOffsetY: 0, regions: [{ x: 0, y: 0, width: w, height: h }] };
  }
  const regions = [];
  if (dx !== 0) regions.push({ x: dx > 0 ? w - dx : 0, y: 0, width: Math.abs(dx), height: h });
  if (dy !== 0) regions.push({ x: Math.max(0, -dx), y: dy > 0 ? h - dy : 0, width: w - Math.abs(dx), height: Math.abs(dy) });
  return { pixelOffsetX: dx, pixelOffsetY: dy, regions };
}
